import { LucideCaptions, LucideCaptionsOff } from "lucide-react"
import { useMemo, useState } from "react"
import {
  aCaptions,
  aCurrentCaption,
  aIsCaptionsOpen,
  useAtom,
  useAtomValue,
} from "~/atoms"
import { Tooltip } from "~/components/ui"
import useIsPhone from "~/hooks/useIsPhone"
import { Caption } from "~/types"
import { cn } from "~/utils"
import { ControlButton } from "./control-button"
import ControlPopoverContent from "./control-popover-content"

export default function CaptionControl({ className }: { className?: string }) {
  const [isCaptionsOpen, setIsCaptionsOpen] = useAtom(aIsCaptionsOpen)
  const captions = useAtomValue(aCaptions)
  const [currentCaption, setCurrentCaption] = useAtom(aCurrentCaption)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const isPhone = useIsPhone()

  const options = useMemo(() => {
    return [
      {
        label: "Off",
        selected: !currentCaption?.srtUrl,
        value: null,
      },
      ...(captions || []).map((caption) => ({
        label: caption.label,
        value: caption,
        selected: caption.language === currentCaption?.language,
      })),
    ]
  }, [captions, currentCaption])

  if (captions.length === 0) return null

  return (
    <div className="relative flex items-center">
      <Tooltip.Provider>
        <Tooltip delayDuration={200} open={isTooltipOpen}>
          <Tooltip.Trigger asChild>
            <ControlButton
              className={cn(className, isPhone && "!h-8 !px-3")}
              aria-label={currentCaption ? "Hide Captions" : "Show Captions"}
              aria-expanded={captions.length > 1 ? isCaptionsOpen : undefined}
              aria-haspopup={captions.length > 1 ? "menu" : undefined}
              onMouseOver={() => { if (!isCaptionsOpen) setIsTooltipOpen(true) }}
              onMouseLeave={() => setIsTooltipOpen(false)}
              onFocus={() => { if (!isCaptionsOpen) setIsTooltipOpen(true) }}
              onBlur={() => setIsTooltipOpen(false)}
              onClick={(e) => {
                e.stopPropagation()
                setIsTooltipOpen(false)
                if (captions.length === 1) {
                  setCurrentCaption(currentCaption ? null : captions[0])
                } else {
                  setIsCaptionsOpen(!isCaptionsOpen)
                }
              }}
            >
              {captions.length > 1 || !currentCaption ? (
                <LucideCaptions className="h-6 w-6 text-white" />
              ) : (
                <LucideCaptionsOff className="h-6 w-6 text-white" />
              )}
            </ControlButton>
          </Tooltip.Trigger>
          <Tooltip.Content>
            {currentCaption ? "Hide Captions" : "Show Captions"}
          </Tooltip.Content>
        </Tooltip>
      </Tooltip.Provider>

      <ControlPopoverContent<Caption | null>
        title="Subtitles/CC"
        isOpen={captions.length > 1 ? isCaptionsOpen : false}
        onClose={() => setIsCaptionsOpen(false)}
        options={options}
        onClick={(value) => {
          setCurrentCaption(value)
          setIsCaptionsOpen(false)
        }}
      />
    </div>
  )
}
